// ONE ELEMENT TREE, CHROME TOGGLED BY CSS (PO 2026-07-31, hardened 2026-07-31).
//
// The bottom tab bar, the pill top nav and the dark sidebar are all rendered
// unconditionally and shown/hidden with breakpoint classes, around a SINGLE
// `<Outlet/>`. That is a correctness requirement, not tidiness.
//
// The first cut of the responsive conversion branched in JS —
// `isPhone ? <MobileAppShell/> : <Shell/>` — in all three surface shells.
// Those are different component types, so every viewport cross past the
// breakpoint unmounted the entire page subtree. Rotating an iPhone 14 Pro Max
// (430 → 932px) mid-task therefore wiped every unsaved field on the page: the
// RTW plan form, a first-response draft, half-filled preferences. The chat
// surfaces were patched individually by moving their state into the query
// cache, but that only fixed the instances anyone had thought of — the class
// of bug stayed open for every form on every `/manager/*`, `/portal/*` and
// `/hr/*` page. Keeping the tree constant closes it everywhere at once, and
// keeps it closed for pages nobody has written yet.
//
// Any future layout difference here belongs in a `md:`/`lg:` class or a
// className prop, NEVER in a JS branch that returns a different tree.
//
// Scrolling lives on `<main>` rather than the document at every width. The app
// has no `<ScrollRestoration>`, so nothing depended on document scroll, and it
// means the phone layout's pinned tab bar and the full-bleed chat's internal
// scroller work without fixed positioning or magic-number height maths.

import { useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { AppSidebar, MobileTopNav, type NavItem } from '@/components/AppNav.tsx';
import { MobileTabBar, type MobileTab } from '@/components/MobileTabBar.tsx';

// Human-facing role label. The raw enum (`HR_Admin`) never reaches the user.
// Exported for the mobile app shell's account block, which shows the same
// pill as the desktop sidebar.
export const ROLE_LABELS: Record<string, string> = {
  HR_Admin: 'HR Admin',
  HR_Viewer: 'HR Viewer',
  Manager: 'Line Manager',
  Employee: 'Employee',
};

interface ShellProps {
  /** Uppercase section label above the nav (e.g. `MANAGER`, `HR TOOLS`). */
  chipLabel: string;
  nav: NavItem[];
  // The id of the parent route whose loader returned the SessionClaims.
  routeId: string;
  // Where to send the user after sign-out. Defaults to the tenant sign-in; the
  // BesideU Admin and CGC tiers use their own dedicated entry points.
  signOutPath?: string | undefined;
  /**
   * Bottom tab bar for the app-shell chrome. Omit for a surface that has no
   * phone layout (admin / CGC) and the shell renders desktop chrome only.
   */
  tabs?: MobileTab[] | undefined;
  /** `aria-label` for the tab bar — the surface name, e.g. `Manager`. */
  tabsLabel?: string | undefined;
  /**
   * Where the bottom tab bar gives way to desktop chrome. `md` for Manager and
   * HR; `lg` for the Employee portal, whose dark rail only renders at lg+ so
   * anything below it would otherwise be a nav-less gap.
   */
  breakpoint?: 'md' | 'lg';
  /**
   * Classes on the wrapper INSIDE the scroll container — page padding and max
   * width. Route-dependent for a full-bleed page; changing it is a className
   * swap on a stable element, never a remount.
   */
  contentClassName?: string;
  /** `'employee'` re-points the primary tokens to the softer Employee Blue. */
  surface?: 'employee' | undefined;
}

// Static class strings per breakpoint — Tailwind can't see interpolated ones.
// `MobileTopNav` carries its own `lg:hidden`, so the wrapper only has to hide
// it BELOW the tab-bar breakpoint; at `lg` the range is empty and it never
// shows at all.
const BREAKPOINT_CLASSES = {
  md: { tabBar: 'md:hidden', topNav: 'hidden md:block' },
  lg: { tabBar: 'lg:hidden', topNav: 'hidden' },
} as const;

/**
 * `contentClassName` for a page that goes full-bleed (edge-to-edge, internally
 * scrolling — a Maeve chat thread, currently) below the shell's own
 * `breakpoint`, and a bounded, padded column at it. `ManagerShell` (`md`,
 * `/manager/maeve`) and `PortalShell` (`lg`, `/portal/maeve`) each used to
 * hand-roll this same string; shared here so the `dvh`-sizing rationale and
 * the breakpoint-prefix logic can't drift between the two as new full-bleed
 * pages are added. Literal per-breakpoint strings, not built by
 * interpolation — Tailwind's class scanner needs each token to appear
 * verbatim in source to generate it, same reason `BREAKPOINT_CLASSES` above
 * is a static table rather than a template.
 *
 * Still local to each caller: WHICH paths are full-bleed (`FULL_BLEED_PATHS`)
 * — that's page-specific data, same as `MANAGER_NAV`/`PORTAL_NAV`.
 */
export const FULL_BLEED_CONTENT_CLASSNAME: Record<'md' | 'lg', string> = {
  md: 'h-full md:mx-auto md:h-auto md:w-full md:max-w-6xl md:px-4 md:py-6 lg:px-8 lg:py-10',
  lg: 'h-full lg:mx-auto lg:h-auto lg:w-full lg:max-w-3xl lg:px-8 lg:py-10',
};

/**
 * Sign-out mutation. Extracted from `Shell` because the mobile app shell
 * has no sidebar below its breakpoint to hang a sign-out button on, so the
 * "More"/Team pages need their own — and the `queryClient.clear()` below is
 * the kind of thing that must not be reimplemented from memory at a second
 * call site.
 *
 * `signOutPath` defaults to the tenant sign-in; the BesideU Admin and CGC
 * tiers pass their own entry points.
 */
export function useSignOut(signOutPath = '/sign-in') {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    onSuccess: async () => {
      // Drop the entire query cache — not just ['me'] — so a second user on
      // the same browser doesn't briefly see the previous user's cases/users
      // before the next fetch resolves.
      queryClient.clear();
      await navigate(signOutPath);
    },
  });
}

export function Shell({
  chipLabel,
  nav,
  routeId,
  signOutPath = '/sign-in',
  tabs,
  tabsLabel,
  breakpoint = 'md',
  contentClassName = 'mx-auto w-full max-w-6xl px-4 py-5 md:py-6 lg:px-8 lg:py-10',
  surface,
}: ShellProps) {
  // Reads from the route loader cache — see useSession in lib/auth.ts. The
  // session drives the sidebar user pill (name + role) as well as serving as
  // a runtime assertion that the loader actually ran.
  const session = useSession(routeId);
  const logout = useSignOut(signOutPath);
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // Scroll the new page to the top on navigation.
  //
  // Nothing did this before, and no built-in does it for us: the app has no
  // `<ScrollRestoration>`, and it would not help if it did — that restores
  // WINDOW scroll, and scrolling lives on the `<main>` below at every width
  // (see this file's header). Because the shell keeps one element tree across
  // every in-shell navigation, `<main>` never unmounts, so its `scrollTop`
  // simply persisted from the previous page: leave a long case list scrolled
  // halfway and the next page opened halfway down, or wherever the browser
  // clamped that offset to. Only a route that swapped the whole shell out
  // (which used to include `/manager/welcome`) ever started at the top, and
  // that was an accident of remounting rather than anything anyone asked for.
  //
  // Keyed on `pathname` ALONE, deliberately — not `location.key`, not
  // `search`. Search params here are in-PAGE state, not navigation: `Tabs.tsx`
  // mirrors the active tab into the URL, and `use-list-search-params.ts` (plus
  // the HR/admin list pages that hand-roll the same thing) mirrors the
  // debounced search box, the status filter and the pager — all via
  // `setSearchParams(..., { replace: true })`. Every one of those produces a
  // fresh `location.key` and a changed `search`, so keying on either would
  // yank the page to the top on each keystroke into a search box and on every
  // tab switch inside a support pathway.
  //
  // Layout effect, not `useEffect`: it runs before paint, so the incoming page
  // is never briefly painted at the outgoing page's offset. There is no
  // `scroll-behavior: smooth` anywhere in the stylesheet, so this is instant
  // and needs no reduced-motion handling.
  //
  // All three claims above are pinned by `e2e/shell-scroll.spec.ts`, which was
  // checked against a deliberately broken `Shell` in both directions: removing
  // the reset fails the navigation test, and widening the dep array to include
  // `search` fails the tab-switch and search-box tests.
  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  const name = 'name' in session ? session.name : '';
  const user = { name, roleLabel: ROLE_LABELS[session.role] ?? chipLabel };
  const navProps = {
    eyebrow: chipLabel,
    nav,
    user,
    onSignOut: () => logout.mutate(),
    signingOut: logout.isPending,
    // No `helpHref` until a real help-centre URL exists — a placeholder
    // `#` renders a dead link that just scrolls to top.
  };
  const bp = BREAKPOINT_CLASSES[breakpoint];

  return (
    <div
      {...(surface ? { 'data-surface': surface } : {})}
      className="flex h-dvh bg-background text-foreground"
    >
      <AppSidebar {...navProps} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className={bp.topNav}>
          <MobileTopNav {...navProps} />
        </div>
        {/* The scroll container at every width. A full-bleed page passes a
            wrapper that is exactly `h-full`, so this never actually overflows
            there and the page's own internal scroller is the only one — no
            nested-scroller fight when the on-screen keyboard opens. */}
        <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto">
          <div className={contentClassName}>
            <Outlet />
          </div>
        </main>
        {tabs ? (
          <div className={bp.tabBar}>
            <MobileTabBar ariaLabel={tabsLabel ?? chipLabel} tabs={tabs} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Retained for the surfaces that render a role label inline (Portal header,
// admin/CGC chrome) without the full sidebar.
export function RoleChip({ label }: { label: string }) {
  return (
    <span className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
      {label}
    </span>
  );
}
